import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Wrapper } from '@googlemaps/react-wrapper';
import classNames from 'classnames';

import styles from './MiniMapDisplay.module.scss';

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

// Mini Map component - simplified version for card previews
const MiniMap = ({ center, zoom, markers, style, isEmergency }) => {
  const [map, setMap] = useState(null);

  const mapRef = useCallback(
    node => {
      if (node !== null && !map && node instanceof Element) {
        const newMap = new window.google.maps.Map(node, {
          center,
          zoom: zoom || 12,
          mapTypeId: 'roadmap', // Always use roadmap for consistency
          disableDefaultUI: true, // Hide controls for cleaner mini-map
          gestureHandling: 'none', // Disable interaction in preview
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          tilt: 0, // Disable 3D tilt
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }], // Hide POI labels for cleaner view
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }], // Hide transit labels
            },
          ],
        });
        setMap(newMap);
      }
    },
    [center, zoom, style, map]
  );

  // Add markers when map is loaded
  React.useEffect(() => {
    if (map && markers && markers.length > 0) {
      markers.forEach(markerData => {
        const marker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lng },
          map: map,
          title: markerData.title,
          icon: isEmergency
            ? {
                url:
                  'data:image/svg+xml;charset=UTF-8,' +
                  encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <circle cx="12" cy="12" r="10" fill="#ff0000" stroke="#ffffff" stroke-width="2"/>
                <text x="12" y="16" text-anchor="middle" fill="white" font-size="14" font-weight="bold">!</text>
              </svg>
            `),
                scaledSize: new window.google.maps.Size(24, 24),
              }
            : undefined,
        });
      });
    }
  }, [map, markers, isEmergency]);

  return <div ref={mapRef} className={styles.miniMap} />;
};

MiniMap.propTypes = {
  center: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  markers: PropTypes.array,
  style: PropTypes.string,
  isEmergency: PropTypes.bool,
};

// Main MiniMapDisplay component
const MiniMapDisplay = ({ mapData, className, onClick }) => {
  if (!mapData || !mapData.coordinates) {
    return null;
  }

  const isEmergency = mapData.status === 'EMERGENCY' || mapData.incidentType === 'VEHICLE_CRASH';

  const render = status => {
    if (status === 'LOADING') {
      return (
        <div className={classNames(styles.miniMapContainer, className)}>
          <div className={styles.loading}>
            <div className={styles.loadingSpinner} />
            <span>Loading map...</span>
          </div>
        </div>
      );
    }

    if (status === 'FAILURE') {
      return (
        <div className={classNames(styles.miniMapContainer, className)}>
          <div className={styles.error}>
            <span>Map unavailable</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(styles.miniMapContainer, className, {
          [styles.emergency]: isEmergency,
        })}
        onClick={onClick}
        role="button"
        tabIndex={0}
      >
        <MiniMap
          center={mapData.coordinates}
          zoom={mapData.zoom || 12}
          markers={mapData.markers}
          style={mapData.style}
          isEmergency={isEmergency}
        />

        {/* Map overlay with basic info */}
        <div className={styles.mapOverlay}>
          <div className={styles.mapInfo}>
            {isEmergency && <div className={styles.emergencyBadge}>🚨 EMERGENCY</div>}
            <div className={styles.locationText}>📍 {mapData.address || 'Location'}</div>
            {mapData.vehicleId && <div className={styles.vehicleText}>🚐 {mapData.vehicleId}</div>}
          </div>
        </div>

        {/* Click indicator */}
        <div className={styles.clickIndicator}>Click to view details</div>
      </div>
    );
  };

  return <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} libraries={['places']} />;
};

MiniMapDisplay.propTypes = {
  mapData: PropTypes.object.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

MiniMapDisplay.defaultProps = {
  className: '',
  onClick: () => {},
};

export default MiniMapDisplay;
